/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Shared item-shape builders for GA4 `ecommerce.items[]` and the legacy UA
 * `ecommerce.{add,remove,checkout,purchase}.products[]` arrays.
 *
 * Kept out of `add-to-cart.ts` so `view-cart.ts`, `cart-view.ts`,
 * `remove-from-cart.ts`, and the checkout/purchase events can share one item
 * shape without cross-file re-imports of internal helpers.
 *
 * INT-21 Phase F adds:
 *   - GA4 named-parameter customs on `buildEcommerceItem` (`ashley_exclusive`,
 *     `collection`, `room_type`, `protection_plan_*`, `availability`,
 *     `delivery_type`, and the full ticket-mapping table). Named params are
 *     the GA4-approved replacement for the legacy numeric `dimensionNN` keys.
 *   - Numeric `dimensionNN` short-codes on `buildUaProduct` (UA twin only —
 *     GA4 caps event params at 25 so we never mix the two shapes onto items[]).
 *
 * All `c_*` sources are commented against the legacy
 * `PRODUCT_SYSTEM_ATTRIBUTE_MAPPING` / `PRODUCT_CUSTOM_ATTRIBUTE_MAPPING`
 * constants in `int_googletags/cartridge/scripts/googletags/Org_TagManager.js`.
 * Values we cannot source from SCAPI are OMITTED rather than emitted as blanks.
 */

import type { EcommerceItem } from '../data-layer';
import type { ShopperProducts, ShopperBasketsV2 } from '@/scapi';

/** Union covering both PDP `Product` and basket `ProductItem` shapes. */
export type ProductLike = ShopperProducts.schemas['Product'] | ShopperBasketsV2.schemas['ProductItem'];

type ProductItem = ShopperBasketsV2.schemas['ProductItem'];

export function getProductId(item: ProductLike): string | undefined {
    const r = item as Record<string, unknown>;
    if (typeof r.productId === 'string') return r.productId;
    if (typeof r.id === 'string') return r.id;
    return undefined;
}

export function getProductName(item: ProductLike): string {
    const r = item as Record<string, unknown>;
    if (typeof r.productName === 'string') return r.productName;
    if (typeof r.name === 'string') return r.name;
    return '';
}

export function getColor(product: ProductLike): string | undefined {
    const r = product as Record<string, unknown>;
    const vv = r.variationValues as Record<string, string> | undefined;
    return vv?.color ?? vv?.Color;
}

export function getImageUrl(product: ProductLike): string | null {
    const r = product as Record<string, unknown>;
    const groups = r.imageGroups as ShopperProducts.schemas['Product']['imageGroups'] | undefined;
    if (groups) {
        const largeGroup = groups.find((g) => g.viewType === 'large') ?? groups[0];
        const firstImage = largeGroup?.images?.[0];
        return firstImage?.disBaseLink ?? firstImage?.link ?? null;
    }
    const img = r.image as { disBaseLink?: string; link?: string } | null | undefined;
    if (img && typeof img === 'object') {
        return img.disBaseLink ?? img.link ?? null;
    }
    return null;
}

/**
 * Legacy short-code (`c_deliveryMethod`) → GA4 human-readable label.
 * Mirrors the Ashley taxonomy used by Phase E's purchase twin.
 */
const DELIVERY_TYPE_LABELS: Record<string, string> = {
    HD: 'Home Delivery',
    PHD: 'Doorstep Delivery',
    THD: 'Truck Home Delivery',
    DS: 'Direct Ship',
    DS3PL: 'Direct Ship',
    PICKUP: 'Store Pickup',
};

/**
 * Resolve the GTM `brand` value for a product.
 *
 * Legacy SG parity (`Org_TagManager.getProductObject` line 671):
 *   obj.brand = product.custom.directShipThirdPartyFlag ? product.custom.facetBrand : 'Ashley';
 *
 * For first-party Ashley products (the vast majority), brand is always 'Ashley'.
 * Third-party direct-ship products use `c_facetBrand` (e.g. "Tempur Pedic®").
 * The SCAPI standard `brand` field is not used by Ashley's catalog.
 */
export function resolveProductBrand(record: Record<string, unknown>): string {
    const directShipThirdParty =
        record.c_directShipThirdPartyFlag === true || record.c_directShipThirdPartyFlag === 'true';
    if (directShipThirdParty) {
        const facetBrand = record.c_facetBrand;
        if (typeof facetBrand === 'string' && facetBrand.length > 0) return facetBrand;
    }
    return 'Ashley';
}

function readString(record: Record<string, unknown>, key: string): string | undefined {
    const value = record[key];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readNumber(record: Record<string, unknown>, key: string): number | undefined {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
}

function readBoolean(record: Record<string, unknown>, key: string): boolean | undefined {
    const value = record[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
        if (value === 'true') return true;
        if (value === 'false') return false;
    }
    return undefined;
}

/**
 * Legacy dimensionNN → GA4 named-param mapping (from `Org_TagManager.js`
 * `PRODUCT_SYSTEM_ATTRIBUTE_MAPPING` + `PRODUCT_CUSTOM_ATTRIBUTE_MAPPING`).
 *
 * Two source shapes per row:
 *   - `pdp` — the c_* key the PDP `Product` payload carries (SCAPI camelCase
 *     custom-attribute name once resolved).
 *   - `line` — the c_* key persisted on a basket `ProductItem` when the
 *     value is a per-line override (rare — most product attributes live on
 *     the product itself and are inherited).
 *
 * Where a c_* attribute name has not yet been confirmed against BROWSE-04's
 * PDP custom-attribute dump, we leave the name commented and OMIT the field
 * from the emitted payload. See the "unsourced" list at the bottom of this
 * file for the accumulator that Phase H's E2E spec walks.
 */
type AttrKey = { pdp?: string; line?: string };

const NAMED_ATTR_SOURCES: Record<string, AttrKey> = {
    // From PRODUCT_SYSTEM_ATTRIBUTE_MAPPING (dimension23 -> 'brand')
    // Emitted as `item_brand` today via the existing builder — keep parity.

    // From PRODUCT_CUSTOM_ATTRIBUTE_MAPPING (numeric legacy dimension -> GA4 name).
    ashley_exclusive: { pdp: 'c_ashleyExclusiveFlag' }, // dimension28
    collection: { pdp: 'c_collection' }, // dimension36
    room_type: { pdp: 'c_roomType' }, // dimension40
    online_exclusive: { pdp: 'c_onlineExclusiveFlag' }, // dimension26
    event_featured: { pdp: 'c_eventFeaturedFlag' }, // dimension27
    daily_deal: { pdp: 'c_dailyDealFlag' }, // dimension29
    doorbuster: { pdp: 'c_doorbusterFlag' }, // dimension30
    online_special: { pdp: 'c_onlineSpecialFlag' }, // dimension31
    smart_buy: { pdp: 'c_smartBuyFlag' }, // dimension32
    your_style_your_way: { pdp: 'c_yourStyleYourWayFlag' }, // dimension33
    ar_enabled: { pdp: 'c_augmentedRealityFlag' }, // dimension34
    secondary_color: { pdp: 'c_color2' }, // dimension35
    lifestyle: { pdp: 'c_lifestyle' }, // dimension37
    material: { pdp: 'c_material' }, // dimension38
    pattern: { pdp: 'c_pattern' }, // dimension39
    size: { pdp: 'c_size2' }, // dimension41
    youth: { pdp: 'c_youth' }, // dimension42
    barstool_height: { pdp: 'c_barstoolHeight' }, // dimension44
    sub_brand: { pdp: 'c_brand2' }, // dimension45
    dining_seating: { pdp: 'c_diningSeating' }, // dimension46
    fabric: { pdp: 'c_fabric' }, // dimension47
    feature_a: { pdp: 'c_features5' }, // dimension48
    feature_b: { pdp: 'c_features7' }, // dimension49
    foundation_features: { pdp: 'c_foundationFeatures' }, // dimension50
    shelf_count: { pdp: 'c_numberofShelves' }, // dimension51
    outdoor_seating_capacity: { pdp: 'c_outdoorSeatingCapacity' }, // dimension52
    seating_type: { pdp: 'c_seatingType' }, // dimension53
    size_secondary: { pdp: 'c_size3' }, // dimension54
    size_tertiary: { pdp: 'c_size4' }, // dimension55
    sleeper_size: { pdp: 'c_sleepersize' }, // dimension56
    style: { pdp: 'c_style2' }, // dimension57
    table_height: { pdp: 'c_tableHeight' }, // dimension58
    table_shape: { pdp: 'c_tableShape' }, // dimension59
    table_top: { pdp: 'c_tableTop' }, // dimension60
    type_secondary: { pdp: 'c_type2' }, // dimension61
    type_tertiary: { pdp: 'c_type5' }, // dimension62
    is_package: { pdp: 'c_packageFlag' }, // dimension63
    room_package_id: { pdp: 'c_roomPackageID' }, // dimension64
    refinement_color: { pdp: 'c_refinementColor' }, // dimension67
    unit_weight_lbs: { pdp: 'c_unitWeightLbs' }, // dimension68
    // Merchandising taxonomy (dimension10..13) — Ashley emits four levels on
    // legacy but GA4 only has `item_category`, `item_category2`, `item_category3`,
    // `item_category4`, `item_category5`. Those already come from the primary
    // category path in the base builder, so we skip the legacy c_ keys here to
    // avoid double-emitting.
};

/**
 * Reads a Boolean `flag` off the c_* fields as either a real boolean OR a
 * string ("true"/"false"). Ashley PDP customs are sometimes serialized as
 * strings by SCAPI depending on their BM attribute type — normalise on read.
 */
function readFlag(record: Record<string, unknown>, key: string | undefined): boolean | undefined {
    if (!key) return undefined;
    return readBoolean(record, key);
}

/**
 * Fields we always emit as booleans when present.
 */
const BOOLEAN_FIELDS = new Set([
    'ashley_exclusive',
    'online_exclusive',
    'event_featured',
    'daily_deal',
    'doorbuster',
    'online_special',
    'smart_buy',
    'your_style_your_way',
    'ar_enabled',
    'youth',
    'is_package',
]);

/**
 * Fields we always emit as numbers when present.
 */
const NUMBER_FIELDS = new Set(['shelf_count', 'outdoor_seating_capacity', 'unit_weight_lbs']);

/**
 * Populate the GA4 named-parameter customs on an item payload from the
 * PDP `Product` (or basket `ProductItem`) record. Skips any field whose
 * source is unavailable — do NOT emit empty strings.
 *
 * `eventScope` gates confirmation-only extras (`order_delivery_type`) —
 * see `EventScope`.
 */
function applyNamedCustoms(item: Record<string, unknown>, product: ProductLike, eventScope?: EventScope): void {
    const record = product as Record<string, unknown>;
    for (const [target, source] of Object.entries(NAMED_ATTR_SOURCES)) {
        // Prefer the line-item override when both keys exist (rare).
        const key = source.line && source.line in record ? source.line : source.pdp;
        if (!key) continue;
        if (BOOLEAN_FIELDS.has(target)) {
            const val = readFlag(record, key);
            if (typeof val === 'boolean') item[target] = val;
        } else if (NUMBER_FIELDS.has(target)) {
            const val = readNumber(record, key);
            if (typeof val === 'number') item[target] = val;
        } else {
            const val = readString(record, key);
            if (val) item[target] = val;
        }
    }

    // Availability — legacy `dimension14` (`In Stock` | `Out of Stock`).
    // Prefer the SCAPI stock information on ProductItem; fall back to the
    // PDP-shape `inventory.stockLevel` / `inventory.orderable`.
    const inventory = record.inventory as { orderable?: boolean; stockLevel?: number } | undefined;
    const orderable = record.orderable as boolean | undefined;
    if (typeof inventory?.orderable === 'boolean') {
        item.availability = inventory.orderable ? 'In Stock' : 'Out of Stock';
    } else if (typeof orderable === 'boolean') {
        item.availability = orderable ? 'In Stock' : 'Out of Stock';
    }

    // Availability message — free-text label (legacy `dimension115`-ish).
    // See PRODUCT_CUSTOM_ATTRIBUTE_MAPPING notes in Org_TagManager.js.
    const availabilityMessage = readString(record, 'c_availabilityMessage');
    if (availabilityMessage) item.availability_message = availabilityMessage;

    // delivery_type — Ashley taxonomy (legacy `dimension15` / `dimension102`).
    // Source: `c_deliveryMethod` on the ProductItem (line-item persisted by
    // CART-02 enrichment), falling back to the product's own
    // `c_deliveryMethod` on PDP shape.
    const rawDeliveryMethod = readString(record, 'c_deliveryMethod');
    if (rawDeliveryMethod) {
        const label = DELIVERY_TYPE_LABELS[rawDeliveryMethod];
        if (label) item.delivery_type = label;
    }

    // Confirmation-only `order_delivery_type` (legacy `dimension102`):
    //   directShip ? 'DS' : (c_deliveryMethod === 'HD' ? 'PHD' : 'THD')
    // Mirrors `Org_TagManager.getProductObject` `getConfirmationEvent` branch.
    if (eventScope === 'confirmation') {
        const directShip = readFlag(record, 'c_directShip') === true;
        const orderDeliveryType = directShip ? 'DS' : rawDeliveryMethod === 'HD' ? 'PHD' : 'THD';
        item.order_delivery_type = orderDeliveryType;
    }

    // BV / TurnTo ratings — legacy `dimension24` (rating) + `metric1` (count).
    const bvRating = readNumber(record, 'c_bvAverageRating');
    if (typeof bvRating === 'number') item.bv_rating = bvRating;
    const reviewCount = readNumber(record, 'c_turntoReviewCount') ?? readNumber(record, 'c_bvReviewCount');
    if (typeof reviewCount === 'number') item.review_count = reviewCount;

    // Protection plan block — legacy `dimension110` / `dimension112` / `dimension113`.
    // c_* names taken from Org_TagManager.js `protectionPlanInfo` + line-level
    // `custom.protectionPlanSelected` / `custom.protectionPlanType`.
    const ppEligible = readFlag(record, 'c_protectionPlanEligible');
    if (typeof ppEligible === 'boolean') item.protection_plan_eligible = ppEligible;
    const ppTypes = readString(record, 'c_protectionPlanType');
    if (ppTypes) item.protection_plan_types = ppTypes;
    const ppSelected = readFlag(record, 'c_protectionPlanSelected');
    if (typeof ppSelected === 'boolean') item.protection_plan_selected = ppSelected;

    // Product-type flag (`Standard` | `ProductSet` | `Protection Plan`) —
    // legacy `dimension43`. Derived from `Util.getProductType(product)` in
    // Org_TagManager.js, which reads `product.isProductSetProduct()` /
    // `c_isProtectionPlan`. On SCAPI, ProductSet lines expose `type.set`,
    // ProductItems expose `bundledProductItems` or per-line `type` flags.
    const type = record.type as { set?: boolean; bundle?: boolean } | undefined;
    const isProductSet = Boolean(type?.set) || readFlag(record, 'c_isProductSet') === true;
    const isProtectionPlan = readFlag(record, 'c_isProtectionPlan') === true;
    if (isProtectionPlan) item.product_type = 'Protection Plan';
    else if (isProductSet) item.product_type = 'ProductSet';
    else item.product_type = 'Standard';

    // Product set membership — legacy `dimension134` / `dimension135`.
    if (isProductSet) {
        item.is_product_set = true;
        const productSetId = readString(record, 'c_productSetId');
        if (productSetId) item.product_set_id = productSetId;
    }
}

/**
 * Cart-scope decorations reused across GA4 items[] and UA twin products[].
 * Legacy convention: register `cart_id` + `cart_type` at BOTH event scope
 * and item scope so tag builders that key off items can join back without
 * needing to read the parent event.
 */
export interface CartScope {
    cartId?: string;
    cartType?: string;
}

function applyCartScope(item: Record<string, unknown>, cartScope?: CartScope): void {
    if (!cartScope) return;
    if (cartScope.cartId) item.cart_id = cartScope.cartId;
    if (cartScope.cartType) item.cart_type = cartScope.cartType;
}

/**
 * Scope of the event carrying the item shape. Controls legacy delivery-code
 * remaps that only apply at checkout/confirmation:
 *   - `checkout`     — HD is remapped to `PHD` on the item's `dimension15`
 *                      (mirrors `Org_TagManager.getProductObject` L684-698's
 *                      `getCheckoutEvent` branch).
 *   - `confirmation` — same PHD remap, PLUS emit `dimension102` /
 *                      `order_delivery_type` = `DS` | `PHD` | `THD` derived
 *                      from `c_directShip` / `c_deliveryMethod`.
 *   - `cart`         — no remap; the raw `c_deliveryMethod` short-code passes
 *                      through unchanged.
 */
export type EventScope = 'cart' | 'checkout' | 'confirmation';

export interface BuildItemOptions {
    /** Optional cart-scope decorations (cart_id / cart_type). */
    cartScope?: CartScope;
    /**
     * Scope of the emitting event. When `checkout` or `confirmation`, legacy
     * HD is remapped to `PHD` on the item's delivery keys (see
     * `Org_TagManager.getProductObject` L684-698). When `confirmation`, also
     * emits `order_delivery_type` (GA4) / `dimension102` (UA).
     */
    eventScope?: EventScope;
}

/**
 * Build a GA4 `EcommerceItem` from either a PDP Product or a basket ProductItem.
 * Includes the Ashley extras (`img_url`, `personalizedImageURL`, `productURL`,
 * `item_title`, `color`, `totalSavings`, `totalValue`) already emitted by
 * add-to-cart.ts — kept for parity. Phase F layers GA4 named-parameter
 * customs (ashley_exclusive, collection, room_type, protection_plan_*, etc.)
 * on top.
 */
export function buildEcommerceItem(
    product: ProductLike,
    quantity: number,
    price?: number,
    options?: BuildItemOptions
): EcommerceItem {
    const productId = getProductId(product);
    const itemName = getProductName(product);
    const color = getColor(product);
    const imageUrl = getImageUrl(product);

    const item: EcommerceItem = {
        item_id: productId ?? '',
        item_name: itemName,
        item_title: itemName,
        quantity,
        price: price ?? (product as { price?: number }).price,
        color: color ?? '',
    };

    if (imageUrl) {
        item.img_url = imageUrl;
        item.personalizedImageURL = imageUrl;
    }

    if (productId && typeof window !== 'undefined') {
        item.productURL = `${window.location.origin}/product/${productId}`;
    }

    const productRecord = product as Record<string, unknown>;
    item.item_brand = resolveProductBrand(productRecord);
    if (typeof productRecord.primaryCategoryId === 'string') item.item_category = productRecord.primaryCategoryId;
    if (color) item.item_variant = productId ?? color;
    const basePrice = productRecord.basePrice;
    if (typeof basePrice === 'number') {
        item.price_before_discount = basePrice;
        const currentPrice = item.price ?? 0;
        item.discount = basePrice > currentPrice ? basePrice - currentPrice : 0;
    }

    item.totalSavings = String(item.discount ?? '0');
    item.totalValue = String((item.price ?? 0) * quantity);

    // Phase F — GA4 named-parameter customs from the ticket dimension-mapping table.
    applyNamedCustoms(item as unknown as Record<string, unknown>, product, options?.eventScope);
    applyCartScope(item as unknown as Record<string, unknown>, options?.cartScope);

    return item;
}

/**
 * Build a GA4 `EcommerceItem` from a basket line item, honouring the line's
 * own `quantity` / `price` unless overridden. Convenience wrapper used by
 * `view-cart` and `cart-view` so callers can `basket.productItems.map(buildBasketItem)`.
 */
export function buildBasketItem(
    item: ProductItem,
    options?: BuildItemOptions & { quantity?: number; price?: number }
): EcommerceItem {
    return buildEcommerceItem(item, options?.quantity ?? item.quantity ?? 1, options?.price ?? item.price, options);
}

/**
 * Legacy short-code (`c_deliveryMethod`) → GA3 numeric `dimension15` value.
 * Legacy code emits the short code itself (e.g. `HD`, `DS`) rather than the
 * human label, so we do the same here.
 */
function toDimension15(rawDeliveryMethod: string | undefined): string | undefined {
    if (!rawDeliveryMethod) return undefined;
    // DS3PL collapses onto DS for the numeric alias — legacy didn't distinguish.
    if (rawDeliveryMethod === 'DS3PL') return 'DS';
    return rawDeliveryMethod;
}

export interface UaProductOptions {
    /**
     * When false, suppress the numeric `dimensionNN` block on the UA twin.
     * Defaults to `true`. The call site is responsible for threading the
     * config-level `emitLegacyAliases` flag through — the numeric dimensions
     * are legacy GA3 aliases and should share the same gating.
     */
    emitLegacyAliases?: boolean;
    /** 1-based position within the list — emitted as `position` for legacy parity. */
    position?: number;
    /** Cart-scope decorations (cart_id / cart_type). Legacy convention. */
    cartScope?: CartScope;
    /** Ashley `dimension142` (cartId) — narrower legacy alias, matches Org_TagManager. */
    cartId?: string;
    /** Ashley `dimension143` (cartType) — narrower legacy alias. */
    cartType?: string;
    /** Optional coupon codes applied at line-item scope. */
    couponCodesApplied?: string[];
    /** Optional per-line tax `{ currencyCode, amount }`. */
    tax?: { currencyCode: string; amount: number };
    /** Optional per-line total discount `{ currencyCode, amount }`. */
    totalDiscount?: { currencyCode: string; amount: number };
    /**
     * Scope of the emitting event. When `checkout` or `confirmation`, legacy
     * HD is remapped to `PHD` on `dimension15`. When `confirmation`, also
     * emits `dimension102`. See `EventScope`.
     */
    eventScope?: EventScope;
}

/**
 * Populate the numeric `dimensionNN` short-codes on a UA-shape product
 * payload from the PDP `Product` (or basket `ProductItem`) record.
 *
 * These live ONLY on the UA twin (`ecommerce.add.products[]`,
 * `ecommerce.remove.products[]`, `ecommerce.checkout.products[]`,
 * `ecommerce.purchase.products[]`) — never on GA4 `items[]` (25-param cap).
 *
 * `eventScope` gates two legacy-parity remaps that only apply at checkout /
 * confirmation (see `Org_TagManager.getProductObject` L684-698):
 *   - HD → PHD for `dimension15` when eventScope is `checkout` /
 *     `confirmation` (legacy `getCheckoutEvent` branch).
 *   - `dimension102` emitted on `confirmation` = `directShip ? 'DS' :
 *     (HD ? 'PHD' : 'THD')`.
 */
function applyNumericDimensions(payload: Record<string, unknown>, product: ProductLike, eventScope?: EventScope): void {
    const record = product as Record<string, unknown>;

    // dimension23 — brand (PRODUCT_SYSTEM_ATTRIBUTE_MAPPING). Same 'Ashley' default as
    // resolveProductBrand — legacy Org_TagManager.js line 671.
    payload.dimension23 = resolveProductBrand(record);

    // Merchandising categories (dimension10..13).
    const dim10 = readString(record, 'c_merchandisingPrimaryCategory');
    if (dim10) payload.dimension10 = dim10;
    const dim11 = readString(record, 'c_merchandisingSubCategory');
    if (dim11) payload.dimension11 = dim11;
    const dim12 = readString(record, 'c_merchandisingSubSubCategory');
    if (dim12) payload.dimension12 = dim12;
    const dim13 = readString(record, 'c_merchandisingSubSubSubCategory');
    if (dim13) payload.dimension13 = dim13;

    // Availability (dimension14).
    const inventory = record.inventory as { orderable?: boolean } | undefined;
    const orderable = record.orderable as boolean | undefined;
    if (typeof inventory?.orderable === 'boolean') {
        payload.dimension14 = inventory.orderable ? 'In Stock' : 'Out of Stock';
    } else if (typeof orderable === 'boolean') {
        payload.dimension14 = orderable ? 'In Stock' : 'Out of Stock';
    }

    // delivery method short-code (dimension15). PICKUP is intentionally not
    // mapped by Org_TagManager.js — legacy only wrote HD/PHD/THD/DS here.
    const rawDeliveryMethod = readString(record, 'c_deliveryMethod');
    let dim15 = toDimension15(rawDeliveryMethod);
    // Legacy parity — `getCheckoutEvent` branch in Org_TagManager.getProductObject:
    // when the event is a checkout / confirmation event and c_deliveryMethod === 'HD',
    // dimension15 is emitted as 'PHD' instead of 'HD'.
    if (dim15 === 'HD' && (eventScope === 'checkout' || eventScope === 'confirmation')) {
        dim15 = 'PHD';
    }
    if (dim15) payload.dimension15 = dim15;

    // Confirmation-only dimension102 — `directShip ? 'DS' : (HD ? 'PHD' : 'THD')`.
    // Mirrors `Org_TagManager.getProductObject` `getConfirmationEvent` branch.
    if (eventScope === 'confirmation') {
        const directShip = readFlag(record, 'c_directShip') === true;
        payload.dimension102 = directShip ? 'DS' : rawDeliveryMethod === 'HD' ? 'PHD' : 'THD';
    }

    // BV rating (dimension24).
    const bvRating = readNumber(record, 'c_bvAverageRating');
    if (typeof bvRating === 'number') payload.dimension24 = bvRating;

    // Flag dimensions (26..34). Legacy passes booleans through — emit only when present.
    const numericBooleanMap: Record<string, string> = {
        dimension26: 'c_onlineExclusiveFlag',
        dimension27: 'c_eventFeaturedFlag',
        dimension28: 'c_ashleyExclusiveFlag',
        dimension29: 'c_dailyDealFlag',
        dimension30: 'c_doorbusterFlag',
        dimension31: 'c_onlineSpecialFlag',
        dimension32: 'c_smartBuyFlag',
        dimension33: 'c_yourStyleYourWayFlag',
        dimension34: 'c_augmentedRealityFlag',
        dimension42: 'c_youth',
        dimension63: 'c_packageFlag',
    };
    for (const [dim, key] of Object.entries(numericBooleanMap)) {
        const v = readFlag(record, key);
        if (typeof v === 'boolean') payload[dim] = v;
    }

    // String dimensions (35..67 — skipping the boolean rows above).
    const numericStringMap: Record<string, string> = {
        dimension35: 'c_color2',
        dimension36: 'c_collection',
        dimension37: 'c_lifestyle',
        dimension38: 'c_material',
        dimension39: 'c_pattern',
        dimension40: 'c_roomType',
        dimension41: 'c_size2',
        dimension44: 'c_barstoolHeight',
        dimension45: 'c_brand2',
        dimension46: 'c_diningSeating',
        dimension47: 'c_fabric',
        dimension48: 'c_features5',
        dimension49: 'c_features7',
        dimension50: 'c_foundationFeatures',
        dimension52: 'c_outdoorSeatingCapacity',
        dimension53: 'c_seatingType',
        dimension54: 'c_size3',
        dimension55: 'c_size4',
        dimension56: 'c_sleepersize',
        dimension57: 'c_style2',
        dimension58: 'c_tableHeight',
        dimension59: 'c_tableShape',
        dimension60: 'c_tableTop',
        dimension61: 'c_type2',
        dimension62: 'c_type5',
        dimension64: 'c_roomPackageID',
        dimension67: 'c_refinementColor',
    };
    for (const [dim, key] of Object.entries(numericStringMap)) {
        const v = readString(record, key);
        if (v) payload[dim] = v;
    }

    // Numeric dimensions (dimension51, dimension68).
    const dim51 = readNumber(record, 'c_numberofShelves');
    if (typeof dim51 === 'number') payload.dimension51 = dim51;
    const dim68 = readNumber(record, 'c_unitWeightLbs');
    if (typeof dim68 === 'number') payload.dimension68 = dim68;

    // dimension43 — product type (Standard | ProductSet | Protection Plan).
    const type = record.type as { set?: boolean } | undefined;
    const isProductSet = Boolean(type?.set) || readFlag(record, 'c_isProductSet') === true;
    const isProtectionPlan = readFlag(record, 'c_isProtectionPlan') === true;
    payload.dimension43 = isProtectionPlan ? 'Protection Plan' : isProductSet ? 'ProductSet' : 'Standard';

    // Protection plan block (dimension110 / 112 / 113).
    const ppEligible = readFlag(record, 'c_protectionPlanEligible');
    if (typeof ppEligible === 'boolean') payload.dimension110 = ppEligible;
    const ppTypes = readString(record, 'c_protectionPlanType');
    if (ppTypes) payload.dimension112 = ppTypes;
    const ppSelected = readFlag(record, 'c_protectionPlanSelected');
    if (typeof ppSelected === 'boolean') payload.dimension113 = ppSelected;

    // Delivery-days estimate (dimension126). Legacy computes from
    // `Util.getDeliveryDays(pli)`. We forward the persisted line-level
    // `c_deliveryDays` custom if present — otherwise the field is omitted
    // and downstream reports fall back to their zero baseline.
    const deliveryDays = readNumber(record, 'c_deliveryDays');
    if (typeof deliveryDays === 'number') payload.dimension126 = deliveryDays;

    // Product set membership (dimension134 / 135).
    payload.dimension134 = isProductSet;
    if (isProductSet) {
        const productSetId = readString(record, 'c_productSetId');
        if (productSetId) payload.dimension135 = productSetId;
    }
}

/**
 * Build a UA Enhanced Ecommerce product object (`ecommerce.add.products[]` /
 * `ecommerce.remove.products[]`) from a PDP Product or basket ProductItem.
 *
 * Emits the numeric `dimension10`..`dimension143` short-codes that legacy fires
 * (gated behind `options.emitLegacyAliases !== false` for the config-level
 * dual-emit toggle). Metric fields (`metric1` review count, `metric2` original
 * price, `metric3` discount amount) are emitted unconditionally when the source
 * data is available.
 */
export function buildUaProduct(
    product: ProductLike,
    quantity: number,
    price?: number,
    options?: UaProductOptions
): EcommerceItem {
    const productRecord = product as Record<string, unknown>;
    const productId = getProductId(product);
    const itemName = getProductName(product);
    const color = getColor(product);
    const imageUrl = getImageUrl(product);
    const productPrice = productRecord.price;
    const actualPrice = price ?? (typeof productPrice === 'number' ? productPrice : undefined);
    const productBasePrice = productRecord.basePrice;
    const basePrice = (typeof productBasePrice === 'number' ? productBasePrice : undefined) ?? actualPrice;
    const discount = basePrice && actualPrice && basePrice > actualPrice ? basePrice - actualPrice : 0;
    const brand = resolveProductBrand(productRecord);
    const category = typeof productRecord.primaryCategoryId === 'string' ? productRecord.primaryCategoryId : '';

    // Variation display — legacy joins all variation-attribute display values
    // comma-delimited. SCAPI exposes `variationValues` as a `{ attributeId: value }`
    // map; best-effort join of the value column.
    const variationValues = productRecord.variationValues as Record<string, string> | undefined;
    const variationDisplay = variationValues
        ? Object.values(variationValues)
              .filter((v): v is string => typeof v === 'string' && v.length > 0)
              .join(',')
        : '';

    const payload: EcommerceItem = {
        id: productId ?? '',
        name: itemName,
        brand,
        category,
        variant: variationDisplay || color || '',
        price: actualPrice,
        quantity,
        color: color ?? '',
        discount,
        img_url: imageUrl,
        item_brand: brand,
        item_category: category,
        item_id: productId ?? '',
        item_name: itemName,
        item_title: itemName,
        item_variant: productId ?? '',
        personalizedImageURL: imageUrl,
        price_before_discount: basePrice,
        productURL: productId && typeof window !== 'undefined' ? `${window.location.origin}/product/${productId}` : '',
        totalSavings: String(discount),
        totalValue: String((actualPrice ?? 0) * quantity),
    };

    // Legacy 1-based position within the list (`position`).
    if (typeof options?.position === 'number') payload.position = options.position;

    // manufacturerSKU / upc — legacy `Org_TagManager.getProductObject`.
    const manufacturerSku =
        readString(productRecord, 'manufacturerSku') ??
        readString(productRecord, 'c_manufacturerSKU') ??
        readString(productRecord, 'manufacturerSKU');
    if (manufacturerSku) (payload as Record<string, unknown>).manufacturerSKU = manufacturerSku;
    const upc = readString(productRecord, 'upc') ?? readString(productRecord, 'c_upc');
    if (upc) (payload as Record<string, unknown>).upc = upc;

    // Metric block — `metric1` review count, `metric2` original price, `metric3` discount.
    const reviewCount =
        readNumber(productRecord, 'c_turntoReviewCount') ?? readNumber(productRecord, 'c_bvReviewCount');
    if (typeof reviewCount === 'number') (payload as Record<string, unknown>).metric1 = reviewCount;
    const originalPrice = readNumber(productRecord, 'c_originalPrice') ?? basePrice;
    if (typeof originalPrice === 'number') (payload as Record<string, unknown>).metric2 = originalPrice;
    if (typeof originalPrice === 'number' && typeof actualPrice === 'number' && originalPrice > actualPrice) {
        (payload as Record<string, unknown>).metric3 = Number((originalPrice - actualPrice).toFixed(2));
    }

    // shipBy — legacy `productLineItem.custom.fulfillerDisplayName` /
    // per-line `c_productFulfiller` on the SCAPI ProductItem.
    const shipBy =
        readString(productRecord, 'c_productFulfiller') ?? readString(productRecord, 'c_fulfillerDisplayName');
    if (shipBy) (payload as Record<string, unknown>).shipBy = shipBy;

    // itmStatus — legacy `product.custom.itemStatus`.
    const itmStatus = readString(productRecord, 'c_itemStatus') ?? readString(productRecord, 'itemStatus');
    if (itmStatus) (payload as Record<string, unknown>).itmStatus = itmStatus;

    // standardPrice — legacy `productLineItem.custom.standardPrice`. Available
    // per-line only; on PDP Product we fall back to `c_standardPrice`.
    const standardPrice = readNumber(productRecord, 'c_standardPrice');
    if (typeof standardPrice === 'number') (payload as Record<string, unknown>).standardPrice = standardPrice;

    // adjustedNetPrice — legacy computes `pli.adjustedNetPrice / quantity`.
    // On ProductItem we can approximate with priceAfterOrderDiscount /
    // priceAfterItemDiscount when present.
    const adjustedNetPrice =
        readNumber(productRecord, 'priceAfterOrderDiscount') ??
        readNumber(productRecord, 'priceAfterItemDiscount') ??
        actualPrice;
    if (typeof adjustedNetPrice === 'number') {
        (payload as Record<string, unknown>).adjustedNetPrice = Number(adjustedNetPrice.toFixed(2));
    }

    // Per-line coupon codes / tax / totalDiscount — supplied by the caller
    // when it has access to the price adjustment tree (e.g. purchase twin).
    if (options?.couponCodesApplied && options.couponCodesApplied.length > 0) {
        (payload as Record<string, unknown>).couponCodesApplied = options.couponCodesApplied;
    }
    if (options?.tax) (payload as Record<string, unknown>).tax = options.tax;
    if (options?.totalDiscount) (payload as Record<string, unknown>).totalDiscount = options.totalDiscount;

    // Ashley `dimension142` / `dimension143` — cartId / cartType at item scope
    // (legacy convention — see Org_TagManager.js:758-760).
    if (options?.cartId) (payload as Record<string, unknown>).dimension142 = options.cartId;
    if (options?.cartType) (payload as Record<string, unknown>).dimension143 = options.cartType;

    // GA4 named-parameter block (kept for tag-builders that consume the UA
    // twin but want the same joined c_* names GA4 items[] use). Item-scope
    // cart_id / cart_type live here too.
    applyCartScope(payload as unknown as Record<string, unknown>, options?.cartScope);

    // Numeric `dimensionNN` block — gated by legacy-alias flag.
    if (options?.emitLegacyAliases !== false) {
        applyNumericDimensions(payload as unknown as Record<string, unknown>, product, options?.eventScope);
    }

    return payload;
}
